import { useState, useMemo } from 'react';
import { Github } from 'lucide-react';
import { cn } from '@reflex/ui/lib/utils';
import { Input } from '@reflex/ui/components/ui/input';
import { SectionLabel } from '@reflex/ui/components/layout/Typography';
import { useGitHubWebBaseUrl } from '@/hooks/config/useConfig';

export interface TopRepositoriesProps {
  repositories: { repoSlug: string; count: number }[];
  searchPlaceholder?: string;
  showTitle?: boolean;
  className?: string;
  /**
   * Total agent runs in the same window (including agents not tied to a
   * repository). When supplied alongside `repoTaggedRuns`, the card adds
   * a footer reconciling the visible repo total with the user's overall
   * activity — closes the "62 in the heatmap but only 43 in the list"
   * confusion that the activity page had before. Omitting both keeps the
   * older two-prop API for callers that don't track the no-repo bucket.
   */
  totalRuns?: number;
  /** Number of runs that carried a repository attachment. */
  repoTaggedRuns?: number;
}

export function TopRepositories({
  repositories,
  searchPlaceholder = 'Find a repository...',
  showTitle = true,
  className,
  totalRuns,
  repoTaggedRuns,
}: TopRepositoriesProps) {
  const githubWebBaseUrl = useGitHubWebBaseUrl();
  const [search, setSearch] = useState('');
  const filtered = useMemo(() => {
    if (!search.trim()) return repositories;
    const q = search.trim().toLowerCase();
    return repositories.filter((r) => r.repoSlug.toLowerCase().includes(q));
  }, [repositories, search]);

  const hasReconciliation = totalRuns !== undefined && repoTaggedRuns !== undefined;
  const noRepoRuns = hasReconciliation ? Math.max(0, totalRuns - repoTaggedRuns) : null;
  // Runs tied to a repository that fell outside the server's top-N list.
  // `repositories` is already capped server-side, so when its counts sum
  // to less than `repoTaggedRuns` the difference lives in repos we don't
  // render — surface it so the visible list + footers reconcile to the
  // heatmap total instead of silently dropping it.
  const shownRepoRuns = repositories.reduce((sum, r) => sum + r.count, 0);
  const otherRepoRuns =
    repoTaggedRuns !== undefined ? Math.max(0, repoTaggedRuns - shownRepoRuns) : null;

  return (
    <div className={cn('space-y-3', className)}>
      {showTitle && <SectionLabel as="h3">Top repositories</SectionLabel>}
      <Input
        type="search"
        placeholder={searchPlaceholder}
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        aria-label="Search repositories"
        className="h-9"
      />
      <ul className="space-y-0.5">
        {filtered.length === 0 ? (
          <li className="rounded-lg border border-dashed border-border py-8 text-center text-sm text-muted-foreground">
            {repositories.length === 0 ? 'No repositories yet' : 'No matching repositories'}
          </li>
        ) : (
          filtered.map(({ repoSlug, count }) => (
            <li key={repoSlug}>
              <a
                href={`${githubWebBaseUrl}/${repoSlug}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2.5 rounded-lg px-2.5 py-2 text-sm transition-colors hover:bg-muted/60"
              >
                <Github className="h-4 w-4 shrink-0 text-muted-foreground" />
                <span className="min-w-0 flex-1 truncate font-medium">{repoSlug}</span>
                <span className="shrink-0 rounded bg-muted px-1.5 py-0.5 font-mono text-3xs text-muted-foreground">
                  {count} run{count !== 1 ? 's' : ''}
                </span>
              </a>
            </li>
          ))
        )}
      </ul>
      {otherRepoRuns !== null && otherRepoRuns > 0 && !search.trim() ? (
        <p
          className="text-xs text-muted-foreground"
          data-testid="top-repositories-other-repos-footer"
        >
          {otherRepoRuns} run{otherRepoRuns !== 1 ? 's' : ''} in repositories not shown above.
        </p>
      ) : null}
      {noRepoRuns !== null && noRepoRuns > 0 && !search.trim() ? (
        <p className="text-xs text-muted-foreground" data-testid="top-repositories-no-repo-footer">
          {noRepoRuns} additional run{noRepoRuns !== 1 ? 's' : ''} not tied to a repository.
        </p>
      ) : null}
    </div>
  );
}
